import { useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Upload, FileImage, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { MAX_FILE_SIZE, validateFiles } from '@/upload/fileValidator';

interface DropZoneProps {
  onFilesAccepted: (files: File[]) => void;
  disabled?: boolean;
}

export function DropZone({ onFilesAccepted, disabled = false }: DropZoneProps) {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      // Show toast for react-dropzone's own rejections
      if (rejections.length > 0) {
        const count = rejections.length;
        toast.error(t('upload.filesRejected', { count }));
      }

      if (accepted.length === 0) return;

      // Run our custom validator for more specific error messages
      const { valid, invalid } = validateFiles(accepted);

      if (invalid.length > 0) {
        for (const { file, error } of invalid.slice(0, 5)) {
          toast.error(`${file.name}: ${error}`);
        }
        if (invalid.length > 5) {
          toast.error(
            t('upload.moreInvalid', { count: invalid.length - 5 }),
          );
        }
      }

      if (valid.length > 0) {
        onFilesAccepted(valid);
        toast.success(t('upload.filesAdded', { count: valid.length }));
      }
    },
    [onFilesAccepted, t],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'image/tiff': ['.tif', '.tiff'],
        'image/heic': ['.heic'],
        'image/heif': ['.heif'],
        'image/x-canon-cr2': ['.cr2'],
        'image/x-nikon-nef': ['.nef'],
      },
      maxSize: MAX_FILE_SIZE,
      multiple: true,
      disabled,
      noClick: false,
      noKeyboard: false,
    });

  const borderColor = isDragReject
    ? 'border-red-400 dark:border-red-500'
    : isDragActive
      ? 'border-primary-500 dark:border-primary-400'
      : 'border-gray-300 dark:border-gray-600';

  const bgColor = isDragActive
    ? 'bg-primary-50/50 dark:bg-primary-900/20'
    : 'bg-white dark:bg-gray-900';

  return (
    <div
      {...getRootProps()}
      className={`
        card flex cursor-pointer flex-col items-center justify-center
        border-2 border-dashed py-16 transition-colors duration-200
        ${borderColor} ${bgColor}
        ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-primary-400 dark:hover:border-primary-500'}
      `}
    >
      <input {...getInputProps()} />

      {isDragReject ? (
        <>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/30">
            <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400" />
          </div>
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {t('upload.unsupportedFiles')}
          </p>
        </>
      ) : isDragActive ? (
        <>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
            <Upload className="h-8 w-8 animate-bounce text-primary-600 dark:text-primary-400" />
          </div>
          <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {t('upload.dropHere')}
          </p>
        </>
      ) : (
        <>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30">
            <Upload className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('upload.title')}
          </h2>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {t('upload.dragPrompt')}
          </p>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            tabIndex={-1}
          >
            <FileImage className="h-4 w-4" />
            {t('upload.selectPhotos')}
          </button>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            {t('upload.supportedFormats')}
          </p>
        </>
      )}
    </div>
  );
}
